<template>
  <div>
    <v-container v-if="!loadingChart">
      <v-row dense>
        <horizontal-bar-chart
          v-if="chartType === 'bar'"
          style="margin: auto; position: relative; width:95%; height:24vh"
          :chart-data="chartData"
          :options="chartOptions"
        />
        <pie-chart
          v-if="chartType === 'pie'"
          style="margin: auto; position: relative; width:95%; height:28vh"
          :chart-data="chartData"
          :options="chartOptions"
        />
      </v-row>
      <log-dialog
        :headers="headers"
        :logData="logData"
        :showDialog="showDialog"
        :title="dialogTitle"
        :isLoading="loading"
        :csvFilename="csvFilename"
        :courseId="course.id"
      />
    </v-container>
  </div>
</template>

<script>
import HorizontalBarChart from './HorizontalBarChart.vue'
import PieChart from './PieChart.vue'
import LogDialog from './LogDialog.vue'
import {
  getDuration,
  Multiset,
  roundToTwo,
  completionTableHeader,
  completionTitle,
  completionNotAttemptedTitle,
  completionNotAtteptedTableHeader,
  TOTAL_ITEMS,
  sortDataByDate
} from '../Constants'
import moment from 'moment'
import _ from 'lodash'

export default {
  props: {
    course: {
      type: Object,
      required: true
    },
    chartType: {
      type: String,
      default: 'pie',
      required: false
    },
    chartStyle: {
      type: Object,
      default: null,
      required: false
    }
  },
  components: {
    HorizontalBarChart,
    LogDialog,
    PieChart
  },
  data() {
    return {
      chartData: {},
      chartOptions: {},
      loading: true,
      loadingChart: false,
      headers: [],
      logData: [],
      showDialog: false,
      dialogTitle: '',
      csvFilename: ''
    }
  },
  mounted() {
    this.loadingChart = true
    this.fillChartData()
    this.loadingChart = false
  },
  methods: {
    onClickChoice(event, array) {
      if (array.length > 0) {
        this.logData = []
        const label = array[0]._chart.tooltip._data.labels[array[0]._index]
        const metadata = array[0]._chart.tooltip._data.metadata
        const courseName = this.course.name.replace(/\s+/g, '_')

        if (label === 'Completed') {
          this.dialogTitle = completionTitle
          this.headers = completionTableHeader
          this.csvFilename = courseName + '_completion_log'
          this.loading = true
          this.showDialog = true

          let interactionsCopy = Object.assign([], this.course.interactions)
          interactionsCopy.forEach(element => {
            if (element.duration != null && element.duration !== undefined) {
              element.duration = getDuration(element.duration)
            }

            if (element.score != null && element.score !== undefined) {
              element.score = roundToTwo(element.score)
            }

            if (
              element.verb === 'failed' ||
              element.verb === 'passed' ||
              element.verb === 'completed' ||
              element.verb === 'attempted'
            ) {
              this.logData.push(element)
            }
          })
        } else {
          this.dialogTitle = completionNotAttemptedTitle
          this.headers = completionNotAtteptedTableHeader
          this.csvFilename = courseName + '_not_attempted_log'
          this.loading = true
          this.showDialog = true

          this.course.completionStatus.notStarted.forEach(element => {
            this.logData.push({ name: element })
          })
        }
        this.logData = sortDataByDate(this.logData)
        this.loading = false
      }
    },
    fillChartData() {
      let correctResponseCount = 0
      let labels = new Array()
      let metadata = new Array()
      let data = new Array()
      let backgroundColor = new Array()
      const max = this.course.completionStatus.assigned.length

      const completedArr = this.course.completionStatus.completed.concat(
        this.course.completionStatus.inProgress
      )

      metadata.push(completedArr)
      metadata.push(this.course.completionStatus.notStarted)

      labels.push('Completed')
      labels.push('Not Attempted')

      const completedCount =
        this.course.completionStatus.completed.length +
        this.course.completionStatus.inProgress.length

      data.push(completedCount)
      data.push(this.course.completionStatus.notStarted.length)

      backgroundColor.push('#7ed321')
      backgroundColor.push('#ead523')
      //backgroundColor.push('#c9c9ca')

      if (this.chartType === 'bar') {
        this.chartOptions = {
          legend: {
            display: false
          },
          hover: {
            onHover: function(e) {
              e.target.style.cursor = 'pointer'
            }
          },
          onClick: this.onClickChoice,
          responsive: true,
          maintainAspectRatio: false,
          tooltips: {
            callbacks: {
              title: function(tooltipItem, data) {
                return data['labels'][tooltipItem[0]['index']]
              },
              label: function(tooltipItem, data) {
                const metadata = data['metadata']
                let label = null
                if (tooltipItem['index'] === 0) {
                  label = 'completed'
                } else if (tooltipItem['index'] === 1) {
                  label = 'inProgress'
                } else if (tooltipItem['index'] === 2) {
                  label = 'notStarted'
                }

                let toolTip = new Array()
                if (label != null) {
                  toolTip = data['metadata'][label]
                }

                return toolTip
              }
              // afterLabel: function(tooltipItem, data) {
              //   return '(kaka)'
              // }
            },
            backgroundColor: '#FFF',
            titleFontSize: 10,
            titleFontColor: '#000',
            bodyFontColor: '#000',
            bodyFontSize: 12,
            displayColors: false,
            borderWidth: 0.2,
            borderColor: '#000',
            hoverBorderColor: '#000',
            hoverBorderWidth: 10
          },
          scales: {
            xAxes: [
              {
                ticks: {
                  min: 0,
                  suggestedMax: max,
                  stepSize: 5
                },
                scaleLabel: {
                  display: true,
                  labelString: 'Assigned'
                }
              }
            ],
            yAxes: [
              {
                scaleLabel: {
                  display: true,
                  labelString: ''
                },
                stacked: true,
                gridLines: {
                  display: false
                }
              }
            ]
          }
        }
      } else if (this.chartType === 'pie') {
        this.chartOptions = {
          hover: {
            onHover: function(e) {
              e.target.style.cursor = 'pointer'
            }
          },
          onClick: this.onClickChoice,
          legend: {
            display: true,
            position: 'left',
            align: 'center'
          },
          tooltips: {
            callbacks: {
              title: function(tooltipItem, data) {
                const label = data['labels'][tooltipItem[0]['index']]
                const count = data['metadata'][tooltipItem[0]['index']].length
                return label + ' ' + count
              },
              label: function(tooltipItem, data) {
                const items = data['metadata'][tooltipItem['index']]
                let toolTip = new Array()
                items.forEach(element => {
                  if (toolTip.length < TOTAL_ITEMS) {
                    toolTip.push(element)
                  }
                })

                if (items.length > TOTAL_ITEMS) {
                  const diff = items.length - TOTAL_ITEMS
                  toolTip.push(`and ${diff} more`)
                }
                return toolTip
              }
            },
            backgroundColor: '#FFF',
            titleFontSize: 10,
            titleFontColor: '#000',
            bodyFontColor: '#000',
            bodyFontSize: 12,
            displayColors: false,
            borderWidth: 0.2,
            borderColor: '#000',
            hoverBorderColor: '#000',
            hoverBorderWidth: 10
          },
          responsive: true,
          maintainAspectRatio: false,
          cutoutPercentage: 30,
          animation: {
            animateRotate: true,
            animateScale: true
          }
        }
      }

      this.chartData = {
        labels: labels,
        metadata: metadata,
        datasets: [
          {
            label: 'Completion Status',
            backgroundColor: backgroundColor,
            data: data
          }
        ]
      }
    }
  }
}
</script>

<style></style>
